// frontend Cursos.js
import React, { useEffect, useState } from 'react';
import { CURSOS_API_URL, CSV_UPLOAD_CURSO_URL } from '../constants';

const Cursos = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ CURSOID: '', NOMBRELARGOCURSO: '' });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  const API = CURSOS_API_URL;
  const CSV_ENDPOINT = CSV_UPLOAD_CURSO_URL;

  const fetchCursos = async () => {
    setLoading(true);
    setErr(''); setOkMsg('');
    try {
      const r = await fetch(API, {
        // credentials: 'include', // Descomenta si tu backend usa sesión por cookies
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Error de API');
      setItems(j.items);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCursos(); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setOkMsg('');
    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode ? `${API}/${encodeURIComponent(form.CURSOID)}` : API;

      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        // credentials: 'include',
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Error al guardar');

      setForm({ CURSOID: '', NOMBRELARGOCURSO: '' });
      setEditMode(false);
      setOkMsg(editMode ? 'Cambios guardados.' : 'Curso agregado.');
      fetchCursos();
    } catch (e) {
      setErr(e.message);
    }
  };

  const onEdit = (it) => {
    setForm({ CURSOID: it.CURSOID, NOMBRELARGOCURSO: it.NOMBRELARGOCURSO });
    setEditMode(true);
    setErr(''); setOkMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar este curso?')) return;
    setErr(''); setOkMsg('');
    try {
      const r = await fetch(`${API}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        // credentials: 'include',
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Error al eliminar');
      setOkMsg('Curso eliminado.');
      fetchCursos();
    } catch (e) {
      setErr(e.message);
    }
  };

  const onCancel = () => {
    setForm({ CURSOID: '', NOMBRELARGOCURSO: '' });
    setEditMode(false);
    setErr(''); setOkMsg('');
  };

  // Descargar TODOS los cursos desde el backend (/api/cursos/export)
  const downloadAllCursos = async () => {
    setErr(''); setOkMsg('');
    try {
      setDownloading(true);
      const resp = await fetch(`${API}/export`, {
        method: 'GET',
        credentials: 'include', // deja esto si usas cookie de sesión
      });
      if (!resp.ok) throw new Error('No se pudo generar el CSV');

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cursos_export.csv';
      a.click();
      URL.revokeObjectURL(url);
      setOkMsg('Descarga iniciada.');
    } catch (e) {
      setErr(`Error al descargar CSV: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const onCsvFileChange = (e) => {
    const f = e.target.files?.[0];
    setCsvFile(f || null);
    setUploadMsg('');
  };

  const uploadCsv = async () => {
    setUploadMsg(''); setErr(''); setOkMsg('');
    if (!csvFile) return setUploadMsg('Selecciona un archivo .csv primero.');
    if (!csvFile.name.toLowerCase().endsWith('.csv')) return setUploadMsg('El archivo debe ser .csv');
    if (csvFile.size > 5 * 1024 * 1024) return setUploadMsg('Archivo demasiado grande (máx 5MB).');

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', csvFile);

      const resp = await fetch(CSV_ENDPOINT, {
        method: 'POST',
        // credentials: 'include',
        body: formData
      });
      const text = await resp.text();
      let payload;
      try { payload = JSON.parse(text); } catch { payload = { ok: resp.ok, raw: text }; }

      if (!resp.ok || payload.ok === false) {
        const msg = payload?.error || payload?.message || payload?.raw || 'Error al subir el CSV';
        throw new Error(msg);
      }

      setUploadMsg('CSV subido correctamente.');
      setCsvFile(null);
      fetchCursos();
    } catch (e) {
      setUploadMsg(`Error: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Cursos</h1>
          <p className="mt-1 text-sm text-slate-500">Mantenimiento y carga masiva</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
            {items.length} registros
          </span>
          <button
            onClick={fetchCursos}
            disabled={loading}
            className="inline-flex items-center rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 disabled:opacity-60"
          >
            {loading ? 'Actualizando…' : 'Refrescar'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {(err || okMsg) && (
        <div
          className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
            err
              ? 'border-rose-200 bg-rose-50 text-rose-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {err || okMsg}
        </div>
      )}

      {/* Grid: CSV + Form */}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        {/* CSV */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-base font-semibold text-slate-900">Carga masiva (.csv) (REEMPLAZA TODOS LOS CURSOS)</h2>
            <p className="mt-1 text-xs text-slate-500">
              Encabezados: <code className="rounded bg-slate-100 px-1 py-0.5">CURSOID, NOMBRELARGOCURSO</code>
            </p>
          </div>
          <div className="px-4 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onCsvFileChange}
                className="block w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700 sm:w-auto"
              />
              <button
                onClick={uploadCsv}
                disabled={uploading || !csvFile}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
              >
                {uploading ? 'Subiendo…' : 'Subir CSV'}
              </button>
              {/* Reemplazo: Descargar plantilla -> Descargar todos los Cursos */}
              <button
                type="button"
                onClick={downloadAllCursos}
                disabled={downloading}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                title="Exporta todos los cursos a CSV (UTF-8 con BOM)"
              >
                {downloading ? 'Preparando archivo…' : 'Descargar todos los Cursos'}
              </button>
            </div>
            {uploadMsg && <div className="mt-2 text-sm text-slate-600">{uploadMsg}</div>}
          </div>
        </div>

        {/* Alta / edición */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-base font-semibold text-slate-900">
              {editMode ? 'Editar curso' : 'Agregar curso'}
            </h2>
          </div>
          <div className="px-4 py-4">
            <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">CURSOID</label>
                <input
                  name="CURSOID"
                  value={form.CURSOID}
                  onChange={onChange}
                  placeholder="UNI_252A_01_UG_II304"
                  disabled={editMode}
                  required
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">NOMBRELARGOCURSO</label>
                <input
                  name="NOMBRELARGOCURSO"
                  value={form.NOMBRELARGOCURSO}
                  onChange={onChange}
                  placeholder="INVESTIGACIÓN DE OPERACIONES"
                  required
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-2">
                {editMode && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                )}
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                >
                  {editMode ? 'Guardar cambios' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Listado</h2>
        </div>
        <div className="px-2 py-2 sm:px-4 sm:py-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2 font-semibold">CURSOID</th>
                  <th className="px-3 py-2 font-semibold">NOMBRELARGOCURSO</th>
                  <th className="px-3 py-2 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={it.CURSOID} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                    <td className="px-3 py-2 font-mono text-slate-800">{it.CURSOID}</td>
                    <td className="px-3 py-2 text-slate-800">{it.NOMBRELARGOCURSO}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => onEdit(it)}
                          className="rounded-md border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => onDelete(it.CURSOID)}
                          className="rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan="3" className="px-3 py-6 text-center text-slate-500">
                      Sin registros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {loading && <div className="px-3 py-4 text-sm text-slate-500">Cargando…</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cursos;
